import type { StagedAction } from './types'

/** Detected actions carry the certainty that created them. At or above this the planner was sure of it. */
export const HIGH_CONFIDENCE = 0.88

/** Below this, down to the planner's lowest staged actions, a detection reads as low confidence. */
export const MID_CONFIDENCE = 0.75

export type ConfidenceLevel = 'low' | 'mid' | 'high'

/** `metadata.source` on actions planner v2 stages (the gateway's `PLANNER_V2_SOURCE`). */
export const PLANNER_V2_SOURCE = 'planner-v2'

export function isPlannerV2Action(action: Partial<Pick<StagedAction, 'metadata'>>): boolean {
  return action.metadata?.source === PLANNER_V2_SOURCE
}

/** Which planner staged an action. `other` is everything no planner staged: chat turns, skills, webhooks. */
export type PlannerVersion = 'v1' | 'v2' | 'other'

/**
 * Only planner runs stamp `sourceMetadata.plannerTriggeredAt`, so with the v2
 * source ruled out it marks a v1 meeting-planner action. v1 rows from before
 * that stamp existed read as `other`.
 */
export function plannerVersion(action: Partial<Pick<StagedAction, 'metadata' | 'sourceMetadata'>>): PlannerVersion {
  if (isPlannerV2Action(action)) {
    return 'v2'
  }
  return action.sourceMetadata?.plannerTriggeredAt ? 'v1' : 'other'
}

/** `metadata` is string-typed end to end, so the confidence travels as a string. */
export const CONFIDENCE_METADATA_KEY = 'confidence'

export function actionConfidence(action: Pick<StagedAction, 'metadata'>): number | undefined {
  const raw = action.metadata?.[CONFIDENCE_METADATA_KEY]
  const value = raw === undefined ? NaN : Number(raw)
  return Number.isFinite(value) ? value : undefined
}

/** How sure the planner was, in thirds; undefined for an action without a recorded confidence. */
export function confidenceLevel(action: Pick<StagedAction, 'metadata'>): ConfidenceLevel | undefined {
  const confidence = actionConfidence(action)
  if (confidence === undefined) return undefined
  if (confidence >= HIGH_CONFIDENCE) return 'high'
  return confidence >= MID_CONFIDENCE ? 'mid' : 'low'
}
